import { Plural, Trans } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";

import { DataModel } from "../../../../models/data-model";
import { Button } from "../../button";
import { useFormContext } from "../../form/form";

/**
 * Multi-edit controls rendered inline in the filter toolbar's actions area.
 * Must be inside the table's <Form> — Save submits it and the saving state
 * comes from the form context.
 */
export const MultiEditToolbarActions = observer(
  function MultiEditToolbarActions({ model }: { model: DataModel }) {
    const saving = !!useFormContext()?.pending;
    const count = model.multiEditDrafts.size;

    // Warn before navigating away while drafts exist.
    useEffect(() => {
      if (count === 0) return;
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
      };
      window.addEventListener("beforeunload", handler);
      return () => {
        window.removeEventListener("beforeunload", handler);
      };
    }, [count]);

    if (count === 0) return null;

    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium whitespace-nowrap">
          {saving ? (
            <Plural
              value={count}
              one="Editing # row — saving…"
              other="Editing # rows — saving…"
            />
          ) : (
            <Plural value={count} one="Editing # row" other="Editing # rows" />
          )}
        </span>
        <Button type="submit">
          <Trans>Save {count}</Trans>
        </Button>
        <Button
          variant="subtle"
          onClick={() => model.stopAllMultiEditing()}
          disabled={saving}
        >
          <Trans>Discard all</Trans>
        </Button>
      </div>
    );
  },
);
